"use client"

import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { Card, CardContent } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Calendar, MapPin, Users, ChevronRight, Search, PartyPopper, Plus, Lock } from "lucide-react"
import Link from "next/link"
import { format } from "date-fns"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { CreatePartyModal } from "@/components/create-party-modal"

interface SurfParty {
  id: string
  title: string
  party_date: string
  start_time: string | null
  time_of_day: string | null
  activity_type: "surf" | "kite"
  is_public: boolean
  organizer_id: string
  organizer_name: string
  organizer_avatar: string | null
  participant_count: number
  is_participant: boolean
  surf_spots: {
    id: string
    name: string
    country: string | null
  }
}

export function SurfPartiesExplore() {
  const { user, loading: authLoading } = useAuth()
  const [parties, setParties] = useState<SurfParty[]>([])
  const [filteredParties, setFilteredParties] = useState<SurfParty[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  
  const supabase = createClient()

  const countries = Array.from(new Set(parties.map(p => p.surf_spots.country).filter(Boolean))) as string[]

  useEffect(() => {
    if (authLoading) return

    const fetchParties = async () => {
      setLoading(true)
      try {
        // Fetch current user's internal ID if logged in
        let currentUserDbId = null
        
        if (user) {
          const { data: userData } = await supabase
            .from("users")
            .select("id")
            .eq("auth_id", user.id)
            .single()
          currentUserDbId = userData?.id
        }

        const { data: partiesData, error: partiesError } = await supabase
          .from("surf_parties")
          .select(`
            id,
            title,
            party_date,
            start_time,
            time_of_day,
            activity_type,
            is_public,
            organizer_id,
            surf_spots (id, name, country),
            organizer:users!surf_parties_organizer_id_fkey(id, name, first_name, last_name, avatar_url, email)
          `)
          .gte("party_date", new Date().toISOString().split("T")[0])
          .order("party_date", { ascending: true })

        if (partiesError) throw partiesError

        const partyIds = partiesData?.map((p) => p.id) || []
        const { data: participantsData } = await supabase
          .from("surf_party_participants")
          .select("party_id, user_id")
          .in("party_id", partyIds)

        const partiesWithCounts = partiesData?.map((party) => {
          const participants = participantsData?.filter((p) => p.party_id === party.id) || []
          const organizer = party.organizer as any
          const organizerName = 
            (organizer?.first_name && organizer?.last_name)
              ? `${organizer.first_name} ${organizer.last_name}`
              : organizer?.name || organizer?.email?.split("@")[0] || "Unknown"
          
          return {
            id: party.id,
            title: party.title,
            party_date: party.party_date,
            start_time: party.start_time,
            time_of_day: party.time_of_day,
            activity_type: party.activity_type || "surf",
            is_public: party.is_public,
            organizer_id: party.organizer_id,
            organizer_name: organizerName,
            organizer_avatar: organizer?.avatar_url || null,
            participant_count: participants.length,
            is_participant: currentUserDbId ? participants.some((p) => p.user_id === currentUserDbId) : false,
            surf_spots: party.surf_spots as any
          }
        }) || []

        setParties(partiesWithCounts as any)
        setFilteredParties(partiesWithCounts as any)

      } catch (error) {
        console.error("[SurfPartiesExplore] Error:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchParties()
  }, [user, authLoading, supabase, refreshTrigger])

  useEffect(() => {
    const query = searchQuery.toLowerCase()
    const filtered = parties.filter(party => {
      const matchesSearch = 
        party.title.toLowerCase().includes(query) ||
        party.organizer_name.toLowerCase().includes(query) ||
        party.surf_spots.name.toLowerCase().includes(query)
      
      const matchesCountry = !selectedCountry || party.surf_spots.country === selectedCountry
      
      return matchesSearch && matchesCountry
    })
    setFilteredParties(filtered)
  }, [searchQuery, selectedCountry, parties])

  const getTimeDisplay = (party: SurfParty) => {
    if (party.start_time) return party.start_time.slice(0, 5)
    if (party.time_of_day) return party.time_of_day.charAt(0).toUpperCase() + party.time_of_day.slice(1)
    return "All day"
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <PartyPopper className="w-12 h-12 text-teal-500 animate-pulse mb-4" />
        <p className="text-slate-500 font-medium">Loading surf parties...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <Button 
            className="h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 px-6 w-full sm:w-auto"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-5 h-5 mr-2" />
            Organize Party
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search parties, organizers or spots..." 
              className="pl-10 h-12 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 self-end md:self-auto">
            <Badge variant="secondary" className="bg-teal-50 text-teal-600 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1.5 rounded-xl border-0 text-xs font-bold">
              {filteredParties.length} Upcoming Parties
            </Badge>
          </div>
        </div>

        {/* Country Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCountry === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCountry(null)}
            className={`rounded-xl h-8 px-4 text-[10px] font-black uppercase tracking-wider ${
              selectedCountry === null 
                ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900" 
                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            All Countries
          </Button>
          {countries.map((country) => (
            <Button
              key={country}
              variant={selectedCountry === country ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCountry(country)}
              className={`rounded-xl h-8 px-4 text-[10px] font-black uppercase tracking-wider ${
                selectedCountry === country
                  ? "bg-teal-500 text-white hover:bg-teal-600 border-0"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-teal-500 hover:border-teal-200"
              }`}
            >
              {country}
            </Button>
          ))}
        </div>
      </div>

      {filteredParties.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-dashed border-2 border-slate-200 dark:border-slate-800 rounded-[2.5rem] overflow-hidden">
          <CardContent className="p-20 text-center">
            <PartyPopper className="w-16 h-16 text-slate-200 dark:text-slate-800 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">No parties found</h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              {searchQuery ? "Try a different search term or clear the filter." : "Be the first to organize a party in the community!"}
            </p>
            {searchQuery && (
              <Button 
                variant="link" 
                onClick={() => setSearchQuery("")}
                className="mt-4 text-blue-500 font-bold"
              >
                Clear search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3 pb-20">
          {filteredParties.map((party) => (
            <Link 
              key={party.id} 
              href={`/explore?spotId=${party.surf_spots.id}`}
              className="group"
            >
              <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-[1.5px] border-slate-100 dark:border-slate-800/50 rounded-[2rem] p-4 flex items-center gap-4 hover:border-teal-200 dark:hover:border-teal-900/30 transition-all shadow-sm active:scale-[0.98] relative">
                {!party.is_public && (
                  <div className="absolute top-3 right-3 z-10">
                    <Lock className="w-3 h-3 text-slate-300" />
                  </div>
                )}
                
                <div className="w-12 h-12 rounded-2xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform">
                  {party.activity_type === "kite" ? "🪁" : "🏄"}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-sm text-slate-900 dark:text-slate-100 truncate group-hover:text-teal-500 transition-colors">
                      {party.title}
                    </span>
                    <div className="flex items-center gap-1 text-teal-500 shrink-0">
                      <Calendar className="w-3 h-3" />
                      <span className="text-[10px] font-black uppercase tracking-wider">
                        {format(new Date(party.party_date), "MMM d")}
                        {` • ${getTimeDisplay(party)}`}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 truncate">
                      {party.surf_spots.name}
                    </span>
                    <span className="text-[10px] font-bold text-slate-300">•</span>
                    <div className="flex items-center gap-1 text-slate-400 shrink-0">
                      <Users className="w-3 h-3" />
                      <span className="text-[10px] font-bold">{party.participant_count}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5 mt-1">
                    <Avatar className="h-4 w-4 border border-white dark:border-slate-800">
                      <AvatarImage src={party.organizer_avatar || ""} alt={party.organizer_name} />
                      <AvatarFallback className="bg-slate-100 text-slate-400 font-bold text-[6px]">
                        {party.organizer_name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-[10px] font-bold text-slate-400">by {party.organizer_name}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {party.is_participant && (
                    <Badge className="bg-green-500 text-white border-0 text-[8px] font-black uppercase px-1.5 py-0.5 rounded-lg shrink-0">
                      Joined
                    </Badge>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <CreatePartyModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onPartyCreated={() => setRefreshTrigger(prev => prev + 1)}
      />
    </div>
  )
}
